<template>
  <v-sheet outlined color="primary" rounded="t-lg">
    <v-card flat height="100vh" class="d-flex flex-column">
      <v-card
        flat
        color="primary"
        class="pa-4 d-flex align-center justify-center"
      >
        <img src="@/assets/chesterLogo.png" height="64" class="mr-3" />
        <span class="text-h6 font-weight-bold white--text">Chesco Bot</span>
      </v-card>

      <v-card-text class="flex-grow-1 overflow-y-auto py-6" ref="chatContainer">
        <div v-if="conversation.length === 0">
          <div class="text-center mb-4 mt-16">
            <div class="d-flex align-center justify-center">
              <img src="@/assets/chatbot.png" height="64" class="mr-3" />
            </div>
          </div>

          <p
            class="text-center font-weight-medium text-h4 mb-1 grey--text text--darken-3"
          >
            {{ this.$i18n.t("helpMsg") }}
          </p>
          <p class="text-center text-h5 mb-5 grey--text">
            {{ this.$i18n.t("msg1") }}
          </p>

          <v-row justify="center" class="ma-4">
            <v-chip
              v-for="(item, index) in suggestions"
              :key="index"
              medium
              class="ma-1 text-body-2"
              color="primary"
              text-color="white"
              @click="sendSuggestion(item)"
            >
              {{ item }}
            </v-chip>
          </v-row>
        </div>

        <!-- Conversation messages -->
        <div v-else class="conversation-container">
          <div
            v-for="(message, index) in conversation"
            :key="index"
            class="mb-4"
            :class="message.type === 'user' ? 'text-right' : 'text-left'"
          >
            <v-row :justify="message.type === 'user' ? 'end' : 'start'">
              <v-col
                cols="auto"
                :class="message.type === 'user' ? 'pl-4' : 'pr-4'"
                style="max-width: 70%"
              >
                <v-card
                  :color="
                    message.type === 'user'
                      ? 'blue lighten-4'
                      : 'green lighten-3'
                  "
                  class="pa-3"
                  :class="
                    message.type === 'user' ? 'user-message' : 'bot-message'
                  "
                  elevation="1"
                >
                  <div
                    class="d-flex align-start"
                    :class="message.type === 'user' ? 'flex-row-reverse' : ''"
                  >
                    <v-avatar
                      size="32"
                      :color="message.type === 'user' ? 'primary' : 'grey'"
                      class="ml-2"
                    >
                      <v-icon dark small>
                        {{
                          message.type === "user" ? "mdi-account" : "mdi-robot"
                        }}
                      </v-icon>
                    </v-avatar>

                    <div class="flex-grow-1 mr-2">
                      <div class="text-caption mb-1 font-weight-medium">
                        {{
                          message.type === "user"
                            ? $i18n.t("self")
                            : "Chesco Bot"
                        }}
                      </div>

                      <!-- main answer text (optional) -->
                      <div class="text-body-1" v-if="message.text">
                        <!-- {{ message.text }} -->
                        {{ truncateText(message.text, 150) }}
                      </div>

                      <!-- helper text (optional, smaller + grey) -->
                      <div
                        v-if="message.helperText"
                        class="text-body-1 mt-1 grey--text text--darken-2 font-weight-black"
                      >
                        {{ message.helperText }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Loading indicator when waiting for response -->
          <div v-if="isWaitingForResponse" class="mb-4 text-left">
            <v-row justify="start">
              <v-col cols="auto" class="pl-4" style="max-width: 70%">
                <v-card
                  color="grey lighten-3"
                  class="pa-3 bot-message"
                  elevation="1"
                >
                  <div class="d-flex align-start">
                    <v-avatar size="32" color="grey" class="mr-2">
                      <v-icon dark small>mdi-robot</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="text-caption mb-1 font-weight-medium">
                        Chesco Bot
                      </div>
                      <div class="d-flex align-center">
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="16"
                          width="2"
                          class="mr-2"
                        ></v-progress-circular>
                        <span class="text-body-2 grey--text">
                          {{ this.$i18n.t("ans") }}
                        </span>
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <v-card
        flat
        tile
        class="flex-shrink-0 pa-8"
        style="border-top: 1px solid #e0e0e0"
      >
        <v-card
          outlined
          class="d-flex align-center"
          style="border-radius: 24px; border: 2px solid #e3f2fd"
        >
          <v-text-field
            v-model="userMessage"
            dense
            flat
            solo
            hide-details
            :placeholder="$i18n.t('typeMessage')"
            :disabled="isWaitingForResponse"
            @keyup.enter="sendMessage"
            @focus="onTextFieldFocus"
            @input="onTextFieldInput"
          ></v-text-field>
          <v-btn
            icon
            color="primary"
            @click="sendMessage"
            :disabled="!userMessage.trim() || isWaitingForResponse"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-card>
      </v-card>
    </v-card>
  </v-sheet>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "chatBot",
  data() {
    return {
      userMessage: "",
        resetDebounceTimer: null, 
      lastResetTime: 0, 
    };
  },
  computed: {
    ...mapGetters([
      "getConversation",
      "getIsWaitingForResponse",
      "getTouchCount",
      "getKioskProfile",
      "getSessionId",
    ]),
    conversation() {
      return this.getConversation;
    },
    isWaitingForResponse() {
      return this.getIsWaitingForResponse;
    },
    suggestions() {
      // Fallback suggestions if i18n is not available
      const defaultQuestions =
        "How do I check my case status?,Probate information,What services does Chester County offer?,Jury information,How do I reschedule a court date?";

      // Try to get from i18n, otherwise use default
      const questionsString = this.$t
        ? this.$t("followupQuestionJC") || defaultQuestions
        : defaultQuestions;

      return questionsString
        .split(",")
        .map((question) => question.trim())
        .filter((question) => question.length > 0);
    },
  },

  methods: {
    truncateText(text, maxLength = 150) {
      if (!text || text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + "...";
    },
    onTextFieldFocus() {
      // Start session on first focus interaction
      if (this.getTouchCount === 0) {
        this.$store.commit("setTouchCount", this.getTouchCount + 1);
        console.log("Session Started using Text Field Focus on ChatBot");
        this.sessionStarted = true;
        var date = new Date();
        
        // Increment touch count
        this.$store.commit("setTouchCount", this.getTouchCount + 1);
        
        this.$store.dispatch("addSessionDataToStrapi", {
          applicationId: this.getKioskProfile.data().applicationId,
          actionType: "Session Started",
          applicationSessionId: this.getSessionId,
          timeStamp: date.toISOString(),
          sessionPayload: {
            module: "Default",
            action: "Touch Detected",
            response: "User Session Started",
            timeStamp: date.toISOString(),
          },
        });
        this.$store.dispatch("addInteractionToPostgres", {
          actionType: "Session Started",
          actionPayload: { recognitionType: "Touch" },
        });
      }
    },



    onTextFieldInput() {
      // Reset the kiosk timeout when user is typing
      this.resetKioskTimeout();
    },
    
    resetKioskTimeout() {
      const currentTime = Date.now();
      const MIN_INTERVAL = 5000; // Minimum 5 seconds between reset calls
      
      // Check if enough time has passed since last reset
      if (currentTime - this.lastResetTime < MIN_INTERVAL) {
        // If called too soon, debounce it
        this.debounceResetTimeout();
      } else {
        // Enough time has passed, reset immediately
        this.executeReset();
      }
    },
    
    debounceResetTimeout() {
      // Clear any existing debounce timer
      if (this.resetDebounceTimer) {
        clearTimeout(this.resetDebounceTimer);
      }
      
      // Set a new debounce timer for 3 seconds
      // This means if user keeps typing, it will reset 3 seconds after they stop
      this.resetDebounceTimer = setTimeout(() => {
        this.executeReset();
      }, 3000);
    },
    
    executeReset() {
      // Update the last reset time
      this.lastResetTime = Date.now();
      
      // Clear any pending debounce timer
      if (this.resetDebounceTimer) {
        clearTimeout(this.resetDebounceTimer);
        this.resetDebounceTimer = null;
      }
      console.log("Restting Timeout in execute Reset");
      // Execute the store actions
      this.$store.dispatch("clearResetTimeout").then(() => {
        this.$store.dispatch("resetKiosk");
      });
    },


    async sendMessage() {
      if (!this.userMessage.trim() || this.isWaitingForResponse) return;
      // var date = new Date();
      // this.$store.commit("setTouchCount", this.getTouchCount + 1);
      // if (this.getTouchCount === 1) {
      //   this.$store.dispatch("addSessionDataToStrapi", {
      //     applicationId: this.getKioskProfile.data().applicationId,
      //     actionType: "Session Started",
      //     applicationSessionId: this.getSessionId,
      //     timeStamp: date.toISOString(),
      //     sessionPayload: {
      //       module: "Default",
      //       action: "Touch Detected",
      //       response: "User Session Started",
      //       timeStamp: date.toISOString(),
      //     },
      //   });
      //   this.$store.dispatch("addInteractionToPostgres", {
      //     actionType: "Session Started",
      //     actionPayload: { recognitionType: "Touch" },
      //   });
      // }

      const messageText = this.userMessage.trim();
      this.$store.dispatch("addUserMessage", messageText);
      this.$store.dispatch("setWaiting", true);
      this.userMessage = "";
      this.$nextTick(() => {
        this.scrollToBottom();
      });
      try {
        const response = await this.$store.dispatch("requestAI", messageText);
        // this.$store.dispatch(
        //   "addBotMessage",
        //   response ||
        //     "I'm sorry, I couldn't process your request. Please try again."
        // );

        if (response && (response.text || response.helperText)) {
          // normal case: text and/or helperText from AI
          this.$store.dispatch("addBotMessage", {
            text: response.text, // can be null
            helperText: response.helperText, // can be null
          });
        } else {
          // fallback: nothing usable came back
          this.$store.dispatch("addBotMessage", {
            text:this.$t("sorryMsg"),
            helperText: null,
          });
        }
      } catch (error) {
        console.error("Error getting response:", error);

        // this.$store.dispatch(
        //   "addBotMessage",
        //   "I apologize, I'm having trouble connecting. Please try again."
        // );
        this.$store.dispatch("addBotMessage", {
          text: this.$t("sorryMsg"),
          helperText: null,
        });
      } finally {
        this.$store.dispatch("setWaiting", false);

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    sendSuggestion(text) {

      if (this.getTouchCount === 0) {
        this.onTextFieldFocus(); // Reuse the same session start logic
      }


      this.userMessage = text;
      this.sendMessage();
    },
    scrollToBottom() {
      const container = this.$refs.chatContainer;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    },
  },
};
</script>

<i18n>
{
  "en": {
      "helpMsg": "I'm here to help.",
      "msg1": "Ask me anything about Chester County services",
      "typeMessage": "Type your message here...",
      "self":"You",
      "ans":"Getting your Answer...",
      "sorryMsg":"I'm sorry, I couldn't process your request. Please try again."
    },
  "es":{
      "helpMsg": "Estoy aquí para ayudar.",
      "msg1": "Pregúnteme cualquier cosa sobre los servicios del condado de Chester",
      "typeMessage": "Escribe tu mensaje aquí...",
      "self":"Tú",
      "ans":"Obteniendo tu respuesta...",
      "sorryMsg":"Lo siento, no pude procesar tu solicitud. Inténtalo de nuevo."
    }
}
</i18n>
